import React, { useEffect, useState } from 'react';
import {Table, Input, Space, Tag, Select, Empty, Button, Checkbox, message, Tooltip, Modal} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import axios from 'axios';
import './TFHourRelease.css';
import {EditOutlined, FilePdfOutlined, FlagOutlined, RedoOutlined} from "@ant-design/icons";
import {getConfig} from "../../config";
import {TaskData} from "../../types/TaskData";
import {useProjectContext} from "../../context/ProjectContext";
import {StatusUtils} from "../../utils/StatusUtils";
import LoadingModal from "../../components/loadingModal/LoadingModal";
import {OpenInternalOrderData} from "../../types/OpenInternalOrderData";
import ConstructionOrderCreation
    from "../internalOrders/constructionOrder/constructionOrderCreation/ConstructionOrderCreation";
import {ViewType} from "../../types/ViewType";

const { Option } = Select;
const { API_BASE_URL } = getConfig();

interface MeasureOrderTableProps {
    onSelectRow: (row: TaskData | null) => void; // Pass null if deselected
    refreshKey: number;
}

const TFHourRelease: React.FC<MeasureOrderTableProps> = ({ onSelectRow, refreshKey }) => {
    const [data, setData] = useState<TaskData[]>([]);
    const [originalData, setOriginalData] = useState<TaskData[]>([]); // To store unfiltered data
    const { selectedProject, selectedManagerNo } = useProjectContext();
    const [selectedRow, setSelectedRow] = useState<TaskData | null>(null);
    const [globalSearch, setGlobalSearch] = useState('');
    const [showAllData, setShowAllData] = useState(false);
    const [arraysEqual, setArraysEqual] = useState(false);
    const [korrexLoading, setKorrexLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');
    const [isKorrexModalVisible, setIsKorrexModalVisible] = useState(false);
    const [korrexData, setKorrexData] = useState<TaskData[]>([]);

    useEffect(() => {
        if (selectedProject)    {
            fetchData(selectedProject);
        }
    }, [selectedProject, refreshKey]);

    const fetchData = async (projectNo: string) => {
        try {
            const response = await axios.get<TaskData[]>(
                `${API_BASE_URL}/getPendingTFHourApproval?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}&managerNo=${selectedManagerNo}`
            );
            setOriginalData(response.data);
            setData(response.data);
            setArraysEqual(
                JSON.stringify(originalData) === JSON.stringify(data)
            );
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        setSelectedRow(null); // Reset selected row on data fetch
    };

    const arraysAreEqual = (arr1: any[], arr2: any[]) => {
        if (arr1.length !== arr2.length) return false;
        return arr1.every((item, index) => JSON.stringify(item) === JSON.stringify(arr2[index]));
    };

    const handleCreateKorrex = (record: TaskData) => {
        fetchTaskData(String(record.id));
        setIsKorrexModalVisible(true);
    };

    const fetchPDFAndOpen = async (orderCardId: string) => {
        try {
            setLoadingText("PDF wird geladen...");
            setLoading(true)
            const projectId = parseInt(orderCardId.slice(0, 6), 10);
            const supplement = parseInt(orderCardId.slice(6, 8), 10);
            const protocol = parseInt(orderCardId.slice(8, 10), 10);

            const response = await axios.get(
                `${API_BASE_URL}/getOrderCardByID`, {
                    params: {
                        auth: "VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU",
                        projectId,
                        supplement,
                        protocol
                    }
                }
            );

            const pdfBase64 = response.data.backendMessage.split(',')[1];
            const pdfWindow = window.open('', '_blank');
            pdfWindow?.document.write(
                `<iframe width="100%" height="100%" src="data:application/pdf;base64,${pdfBase64}"></iframe>`
            );
        } catch (error) {
            console.error('Error fetching PDF:', error);
        } finally   {
            setLoading(false);
        }
    };

    const fetchTaskData = async (taskId: string) => {
        setKorrexLoading(true);
        try {
            const response = await axios.get<TaskData>(
                `${API_BASE_URL}/getSpecificNetworkTask`, {
                    params: {
                        auth: 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU',
                        projectNo: selectedProject,
                        type: 'tfHour',
                        networkPlanId: taskId,
                    }
                }
            );
            setKorrexData([response.data]);
        } catch (error) {
            message.error('Fehler beim Laden der Korrex-Daten.');
            console.error('Error fetching TaskData:', error);
        } finally {
            setKorrexLoading(false);
        }
    };

    const handleCheckboxChange = (e: CheckboxChangeEvent) => {
        setShowAllData(e.target.checked);
        setData(e.target.checked ? originalData : originalData.filter(item => item.activityStatus === 'READY'));
    };

    // Filter data based on global search input
    const filteredData = data.filter(item =>
        item.orderCardId.toLowerCase().includes(globalSearch.toLowerCase()) ||
        item.jobCodeDesc?.toLowerCase().includes(globalSearch.toLowerCase()) ||
        item.oglDesc?.toLowerCase().includes(globalSearch.toLowerCase()) ||
        item.faz.toString().includes(globalSearch)
    );

    const rowSelection = {
        type: 'radio' as const, // Single selection
        selectedRowKeys: selectedRow ? [selectedRow.id] : [],
        onChange: (selectedRowKeys: React.Key[], selectedRows: TaskData[]) => {
            setSelectedRow(selectedRows[0] || null);
            onSelectRow(selectedRows[0] || null);
        },
        getCheckboxProps: (record: TaskData) => ({
            disabled: record.activityStatus !== 'READY', // Disable radio button if status is not READY
        }),
    };

    const handleRowClick = (record: TaskData) => {
        if (record.activityStatus === 'READY') {
            const isSelected = selectedRow?.id === record.id;
            const newSelectedRow = isSelected ? null : record;
            setSelectedRow(newSelectedRow);
            onSelectRow(newSelectedRow);
        }
    };

    const handleOk = async (record: TaskData) => {
        setLoadingText("Wareneingang wird gebucht...");
        setLoading(false);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/book24HourRelease`, {
                    params: {
                        auth: 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU',
                        networkPlanId: record.id,
                        orderCardId: record.orderCardId,
                    }
                }
            );
            if (response.data) {
                message.success('Status-Änderung erfolgreich gebucht!');
                setLoading(false);
                if (selectedProject)    {
                    fetchData(selectedProject);
                }
            }
        } catch (error) {
            message.error('Fehler beim Buchen der Änderung.');
            console.error('Error:', error);
            setLoading(false); // End loading
        }
    };

    const columns = [
        {
            title: 'Auftragskarte',
            dataIndex: 'orderCardId',
            key: 'orderCardId',
            sorter: (a: { orderCardId: string }, b: { orderCardId: string }) => a.orderCardId.localeCompare(b.orderCardId),
            render: (orderCardId: string) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button
                        icon={<FilePdfOutlined />}
                        onClick={(event) => {
                            event.stopPropagation();
                            fetchPDFAndOpen(orderCardId);
                        }}
                        type="primary"
                        size="small"
                    >
                        {orderCardId.replace(/(\d{6})(\d{2})(\d{2})/, '$1/$2/$3')}
                    </Button>
                </div>
            ),
        },
        {
            title: 'Produkt',
            dataIndex: 'jobCodeDesc',
            key: 'jobCodeDesc',
            sorter: (a: TaskData, b: TaskData) => (a.jobCodeDesc ?? '').localeCompare(b.jobCodeDesc ?? '')
        },
        {
            title: 'Bauabschnitt',
            dataIndex: 'oglDesc',
            key: 'oglDesc',
            sorter: (a: TaskData, b: TaskData) => (a.oglDesc ?? '').localeCompare(b.oglDesc ?? '')
        },
        {
            title: 'Protokoll-Bez.',
            dataIndex: 'protocolDesc',
            key: 'protocolDesc',
            sorter: (a: TaskData, b: TaskData) => {
                const valueA = a.protocolDesc?.substring(6) ?? '';
                const valueB = b.protocolDesc?.substring(6) ?? '';
                return valueA.localeCompare(valueB);
            },
            render: (text: string) => {
                return text?.substring(6) ?? '';
            },
            width: 250,
        },
        {
            title: 'Start',
            dataIndex: 'faz',
            key: 'faz',
            sorter: (a: TaskData, b: TaskData) => a.faz.localeCompare(b.faz),
            render: (faz: string) => {
                const [year, month, day] = faz.split('-');
                return `${day}.${month}.${year}`;
            },
        },
        {
            title: 'Status',
            dataIndex: 'activityStatus',
            key: 'activityStatus',
            sorter: (a: TaskData, b: TaskData) => (a.activityStatus ?? '').localeCompare(b.activityStatus ?? ''),
            render: (status: 'DONE' | 'READY' | 'NOT_READY' | 'PENDING') => {
                const color = StatusUtils.getColor(status);
                const translatedStatus = StatusUtils.getTranslatedStatus(status);
                return <Tag color={color}>{translatedStatus}</Tag>;
            },
        },
        {
            title: 'Aktion',
            key: 'actions',
            render: (_: any, record: TaskData) => (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button
                        type="primary"
                        disabled={true}
                        icon={<FlagOutlined />}
                        onClick={async () => {
                            await handleOk(record);
                        }}
                        size="small"
                    >
                        Wird bestätigt bei Bestellung
                    </Button>
                    <div>
                        <Tooltip title="Korrex erstellen">
                            <Button
                                disabled={false}
                                type="primary"
                                icon={<RedoOutlined />}
                                onClick={() => handleCreateKorrex(record)}
                                size="small">
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            ),
        }
    ];

    return (
        <div style={{ padding: '20px' }}>
            <Modal
                title="Erstellung Korrex"
                open={isKorrexModalVisible}
                onCancel={() => {
                    setIsKorrexModalVisible(false);
                    setKorrexData([]);
                }}
                footer={null}
                width={800}
            >
                {korrexData.length > 0 && (
                    <ConstructionOrderCreation
                        data={korrexData}
                        viewType={ViewType.KORREX}
                        onRefreshTable={() => {
                            setIsKorrexModalVisible(false);
                            setKorrexData([]);
                            if (selectedProject) {
                                fetchData(selectedProject);
                            }
                        }}
                    />
                )}
            </Modal>
            <LoadingModal
                open={loading}
                text={loadingText}
            />
            <LoadingModal
                open={korrexLoading}
                text="Korrex wird vorbereitet. Daten werden zurückgesetzt..."
            />
            <Input
                placeholder="Suche in Auftragskarte, Produkt, Bauabschnitt oder Start"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                style={{ marginBottom: 16, width: '100%' }}
            />

            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                pagination={{ pageSize: 100 }}
                scroll={{ y: 700 }}
                locale={{
                    emptyText: <Empty description="Keine Auftragskarten zur Beauftragung vorhanden" />
                }}
                onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                })}
                rowClassName={(record) =>
                    selectedRow?.id === record.id ? 'selected-row' : ''
                }
            />
        </div>
    );
};

export default TFHourRelease;
